/**
 * API client with JWT authentication
 */

// API base URL - configurable via environment variable for different deployments
import * as sb from './sandbox'

const API_BASE = import.meta.env.VITE_API_BASE || '/v1'

/** In sandbox mode, /batch/** reads are served by the GET-only public mirror. */
function sandboxPath(endpoint: string): string {
  return sb.isSandbox() ? endpoint.replace(/^\/batch/, '/recruiter/sandbox') : endpoint
}

/**
 * Resolve a dashboard write entirely in the browser.
 *
 * Returns the same shape the real endpoint returns, so callers cannot tell
 * the difference and no component needs a sandbox branch. Anything not
 * explicitly handled resolves to a benign success rather than throwing: an
 * unrecognised write in a DEMO should be a no-op, never a red error toast in
 * front of a prospect.
 */
async function sandboxWrite<T>(endpoint: string, method: string, body?: BodyInit | null): Promise<T> {
  let payload: Record<string, unknown> = {}
  try { payload = body ? JSON.parse(String(body)) : {} } catch { payload = {} }

  const bulk = endpoint.match(/^\/batch\/[^/]+\/candidates\/bulk-stage$/)
  const one = endpoint.match(/^\/batch\/[^/]+\/candidates\/([^/]+)$/)
  const brief = endpoint.match(/^\/batch\/[^/]+\/brief$/)
  const reqs = endpoint.match(/^\/batch\/([^/]+)\/requirements$/)
  const batch = endpoint.match(/^\/batch\/[^/]+$/)

  // Re-weighting criteria is the one write the demo answers with real
  // recomputed scores rather than a local overlay — the ranking has to
  // actually move, and only the server can move it. The toggle is bounded to
  // a single requirement (see sandbox.ts), so this either flips that flag or
  // declines politely; it never invents a score.
  if (reqs) {
    const editable = sb.reweightReqId()
    const ops = (payload.overrides as Array<{ req_id: string; action: string }>) || []
    const target = editable ? ops.find((o) => o.req_id === editable) : undefined
    if (target) {
      // Direction matters. The canned state is "moved to Preferred", so moving
      // it BACK to Required — or resetting — has to turn the flag off again.
      // Keying off "an edit happened" left it stuck on, and the walkthrough
      // needs the visitor to be able to put it back and watch the scores
      // return.
      // The canned state is "this requirement MOVED to Preferred", so a
      // recategorise to PREFERRED turns the flag on, and moving it back to
      // Required — or resetting it — turns the flag off.
      //
      // This keyed off a delete until 2026-08-23. The demote had been swapped
      // out because it moved nobody, but the cause was arithmetic: weights are
      // normalised to sum to 1.0, so demoting one of 38 requirements from 1.0
      // to 0.6 redistributes a fraction of a percent, while deleting shrinks
      // the denominator. Fixed in the demo's INPUT rather than by keeping an
      // edit that demonstrates the wrong product — see CANNED_OVERRIDE in
      // app/routes/recruiter_demo.py.
      const lvl = (target as { new_level?: string }).new_level
      sb.setReweightOff(
        target.action === 'recategorize' && (lvl || '').toUpperCase() === 'PREFERRED')
    }
    // Re-read so the caller gets the server's view of the new state rather
    // than one this function guessed at.
    return await api.getBatchRequirements(reqs[1]) as unknown as T
  }

  if (bulk) {
    const ids = (payload.candidate_ids as string[]) || []
    sb.patchCandidates(ids, { stage: payload.stage as string })
    return { updated: ids.length } as unknown as T
  }
  if (one) {
    sb.patchCandidate(one[1], payload as { stage?: string; disposition?: string; notes?: string })
    return { candidate_id: one[1], ...payload } as unknown as T
  }
  if (brief) return sb.cannedBrief(sb.readOverlay().batch.job_title) as unknown as T
  if (batch && method === 'PATCH') {
    sb.patchBatchFields(payload as { job_title?: string; company_name?: string })
    return payload as unknown as T
  }
  return {} as T
}

// Error response shape from FastAPI is parsed inline in request();
// see that method for the normalization logic. ApiError interface
// removed when request() switched to handling both flat and
// detail-nested error envelopes via `any`-typed parsing.

// Custom error class that includes error code and metadata
export class ApiRequestError extends Error {
  code: string
  /** HTTP status, when the error came from a response.
   *
   *  Needed because `code` is only populated when the server sends a
   *  structured detail. Auth middleware raises `detail="Token expired"` — a
   *  bare string — so code fell back to 'unknown' and isAuthError() returned
   *  false for the single most important auth failure there is. */
  status?: number
  // Legacy format
  limit?: number
  used?: number
  // New payment_required format (Part B)
  deviceUsed?: number
  deviceLimit?: number
  accountUsed?: number
  accountLimit?: number

  constructor(
    message: string,
    code: string = 'unknown',
    extra?: {
      limit?: number
      used?: number
      deviceUsed?: number
      deviceLimit?: number
      accountUsed?: number
      accountLimit?: number
      status?: number
    }
  ) {
    super(message)
    this.name = 'ApiRequestError'
    this.code = code
    this.limit = extra?.limit
    this.used = extra?.used
    this.deviceUsed = extra?.deviceUsed
    this.deviceLimit = extra?.deviceLimit
    this.status = extra?.status
    this.accountUsed = extra?.accountUsed
    this.accountLimit = extra?.accountLimit
  }
}

// Shared shape for batch / demo ranked results (the recruiter results table).
export interface BatchReqLevel {
  total: number; yes: number; partial: number; no: number
  items: Array<{ text: string; verdict: string; evidence: string | null }>
}
export interface BatchResultsData {
  batch_id: string
  status: string
  job_title?: string | null
  company_name?: string | null
  total_candidates: number
  completed_candidates: number
  failed_candidates: number
  candidates: Array<{
    candidate_id: string
    candidate_label: string
    candidate_name: string | null
    disposition: string | null
    stage: string
    job_id: string | null
    rank: number | null
    fittohire_score: number | null
    fittohire_label: string | null
    literal_fit: number | null
    expanded_fit: number | null
    matched_skills: string[] | null
    missing_skills: string[] | null
    requirement_breakdown: {
      required: BatchReqLevel
      preferred: BatchReqLevel
      responsibility: BatchReqLevel
    } | null
    flags: string[]
    has_notes: boolean
    status: string
    error_code: string | null
  }>
}

// Analysis result types
/** The ten v3 detector kinds. `voice_drift` and `summary_cohesion` were listed
 *  here but the v3 engine never emits them; breadth/coherence/no_results/
 *  scattered/spelling/unsupported were missing, so those rendered as raw
 *  snake_case in the modal. */
export type ReadabilityViolationKind =
  | 'breadth' | 'density' | 'length' | 'repetition' | 'unsupported'
  | 'ai_phrasing' | 'no_results' | 'scattered' | 'spelling' | 'coherence'
  // Similarity signals. voice_drift only appears on a CHAINED résumé (tailored
  // or manually iterated) — the engine needs a parent to diff against and stays
  // silent without one.
  | 'summary_cohesion' | 'voice_drift'

export interface ReadabilityViolation {
  kind: ReadabilityViolationKind
  quoted_text: string
  text: string
  severity: 'warn' | 'major'
  fix?: string  // narrator's suggested correction; '' when it has no bank entry
}

/** What the engine measured on its way to a verdict — present whether or not
 *  anything tripped, so a clean read can show its work. */
export interface ReadabilityChecks {
  skills?: number
  skills_norm_p50?: number | null
  skills_norm_p90?: number | null
  skills_demonstrated?: number
  bullets?: number
  bullets_quantified?: number
  marketing_phrases?: number
  career_years?: number | null
}

export interface IntegrityFinding {
  kind: string        // concealed_text | injection_phrase
  detail: string      // how it was hidden, or what it instructs
  text: string        // the hidden text ITSELF — this is the deliverable
  severity: string
  concealed: boolean
}

export interface ReadabilityIntegrity {
  hidden_text: boolean
  machine_instructions: boolean
  hidden_chars: number
  summary: string | null
  findings: IntegrityFinding[]
  score_capped_from?: number | null   // what the résumé scored before the cap
}

export interface ReadabilityReport {
  score: number | null  // null when INSUFFICIENT (too thin to read)
  // NOTHING_FOUND means the engine read the document and fired no signal.
  // It used to be mapped to STRONG server-side so it never reached this
  // union, which meant "we found nothing" and "this résumé is clean"
  // rendered identically at 100. Six of ten demo candidates showed a
  // perfect score with zero findings, which reads as a broken check rather
  // than ten strong candidates.
  label: 'STRONG' | 'AT_RISK' | 'BLOATED' | 'INSUFFICIENT' | 'NOTHING_FOUND'
  verdict: string
  violations: ReadabilityViolation[]
  checks?: ReadabilityChecks  // what was measured, present even when nothing tripped
  benchmark?: string          // breadth rendered as prose
  // Concealed text and machine-directed instructions found in the ORIGINAL
  // FILE — things the extracted plaintext cannot show. Absent when there was
  // no file to examine (paste-text). Present with hidden_text:false when a
  // file WAS examined and is clean, which is a different statement.
  integrity?: ReadabilityIntegrity
  skills_extracted?: number | null
  checked_at: string
  content_hash: string
  source: string
  cached: boolean
}

export interface DerivedResume {
  run_id: string
  kind: 'tailor' | 'edit'
  display_name: string  // resolved: user-chosen if set, else auto-fallback
  auto_name: string  // always the "<source> (tailored).docx" form
  source_job_id: string
  source_job_title: string | null
  source_company_name: string | null
  created_at: string
  completed_at: string | null
  expires_at: string | null
  days_until_expiry: number | null
  download_available: boolean
  reanalyzed_job_id: string | null
}

// Recruiter candidate context (single candidate drill-in from a batch).
// Powers RecruiterCandidateView + cross-batch history panel.
export interface RecruiterNeighbor {
  candidate_id: string
  candidate_name: string | null
  candidate_label: string
}

export interface RecruiterCrossBatchEntry {
  batch_id: string
  candidate_id: string
  job_id: string | null
  job_title: string | null
  company_name: string | null
  fittohire_score: number | null
  fittohire_label: string | null
  disposition: string | null
  created_at: string
}

export interface RecruiterCandidateContext {
  batch_id: string
  job_title: string | null
  company_name: string | null
  candidate_id: string
  candidate_name: string | null
  candidate_label: string
  job_id: string | null
  status: string
  email: string | null
  phone: string | null
  duplicate_of: string | null
  notes: string | null
  disposition: string | null
  stage: string
  prev_candidate: RecruiterNeighbor | null
  next_candidate: RecruiterNeighbor | null
  cross_batch_history: RecruiterCrossBatchEntry[]
  rank_in_batch: number | null
  rank_total: number | null
  added_at: string | null
  last_edited_at: string | null
  scored_at: string | null
}

export interface ExplainabilityRow {
  skill_id: string
  skill_name: string
  required: boolean
  shown: boolean
  credit: number
  reason: string
}

export interface Blocker {
  skill_id: string
  skill_name: string
  importance: 'must_have' | 'preferred'
  reason: string
}

export interface PriorityItem {
  skill_id: string
  skill_name: string
  priority: number
  suggestion: string
}

export interface CoachingSuggestion {
  category: string
  suggestion: string
  impact: 'high' | 'medium' | 'low'
}

export interface EvidenceItem {
  skill_id: string
  skill_name: string
  snippets: string[]
  confidence: number
}

// =================================================================
// INSIGHTS PIPELINE TYPES
// =================================================================

export interface InsightsRequirement {
  skill_id: string
  skill_name: string
  requirement_level: 'MUST' | 'PREFERRED' | 'OPTIONAL'
  rationale: string
  cue_phrase?: string
  cue_distance?: number
}

export interface InsightsScoringBreakdown {
  weighted_score: number
  must_matched: number
  must_total: number
  must_missing: string[]
  preferred_matched: number
  preferred_total: number
  optional_matched: number
  optional_total: number
  risk_level: 'LOW' | 'MEDIUM' | 'HIGH'
  experience_bonus: number
}

export interface InsightsSuggestedEdit {
  section: string
  text: string
  based_on: Array<{
    source: string
    snippet: string
    source_mode?: string
    start?: number
    end?: number
  }>
  disclaimer: string
}

export interface InsightsGap {
  skill_id: string
  skill_name: string
  gap_type: 'MISSING_MUST' | 'MISSING_PREFERRED' | 'WEAK_EVIDENCE'
  why_it_matters: string
  related_skills_you_have: string[]
  suggested_additions: InsightsSuggestedEdit[]
}

export interface InsightsStrength {
  skill_id: string
  skill_name: string
  years_experience?: number
  proficiency_level?: string
  evidence_snippets: string[]
}

export interface InsightsData {
  requirements: InsightsRequirement[]
  scoring_breakdown: InsightsScoringBreakdown
  gaps: InsightsGap[]
  strengths: InsightsStrength[]
}

export interface SkillBreakdownItem {
  skill_id: string
  skill_name?: string
  concept_key?: string
  type: 'exact' | 'satisfies' | 'equivalent' | 'dee' | 'subsumption' | 'family' | 'missing'
  credit: number
  matched_by?: string | string[]
  subsumption_label?: string
  family_display?: string
}

// =================================================================
// GRAPH UI TYPES
// =================================================================

export interface BoilerplateStats {
  sections_denied: number
  examples: string[]
}

export interface RelatedSuggestion {
  suggested_ck: string
  display_name: string
  domain_id: string
  max_weight: number
  because_of: Array<{ concept_key: string; display_name: string }>
}

// =================================================================
// LLM ENRICHMENT TYPES
// =================================================================

export interface LLMClusterLabel {
  label: string
  suggestion_cks: string[]
}

export interface LLMSuggestionWhy {
  suggested_ck: string
  why: string
}

export interface LLMCopyRewrite {
  verdict_narrative: string
  tone: 'direct' | 'encouraging' | 'cautious'
  original_verdict: string
}

export interface LLMEnrichment {
  cluster_labels: LLMClusterLabel[]
  suggestion_whys: LLMSuggestionWhy[]
  copy_rewrite: LLMCopyRewrite | null
  llm_failed: boolean
  failure_reason: string
  provenance: {
    model: string
    prompt_hash: string
    cost_usd: number
  }
}

export interface ExtractionDetail {
  skill_id: string
  evidence: Array<{
    text: string
    start: number
    end: number
    source_mode: string
  }>
}

export interface RecruiterNarrative {
  job_id: string
  candidate_name: string | null
  narrative: string | null
  generated_at: string | null
}

/**
 * Is this JWT past its own `exp`?
 *
 * Read-only inspection of the payload — the client cannot verify a signature
 * and does not try to. The server remains the authority; this only stops us
 * confidently sending something we can already see is dead.
 *
 * Anything unparseable is treated as NOT expired. A token we cannot read is
 * the server's call to reject, and guessing "expired" here would sign people
 * out over a payload shape we did not anticipate.
 *
 * 30s of leeway absorbs clock skew between the browser and the API, so a
 * token that is about to die is not used for a request that will land after
 * it does.
 */
function isTokenExpired(token: string): boolean {
  try {
    const part = token.split('.')[1]
    if (!part) return false
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
    const exp = JSON.parse(json)?.exp
    if (typeof exp !== 'number') return false
    return exp * 1000 <= Date.now() + 30_000
  } catch {
    return false
  }
}


class ApiClient {
  private token: string | null = null
  // Track if we believe we're authenticated (cookie or localStorage)
  private authenticated: boolean = false

  setToken(token: string | null) {
    this.token = token
    if (token) {
      // Keep localStorage as fallback for environments where cookies don't work
      localStorage.setItem('fth_token', token)
      this.authenticated = true
    } else {
      localStorage.removeItem('fth_token')
      this.authenticated = false
    }
  }

  /**
   * The token to send, preferring whatever localStorage holds NOW.
   *
   * The in-memory copy used to win outright, and it goes stale in the one
   * flow that depends on it most. The anonymous magic-link handoff opens a
   * SECOND tab, which signs in and writes a fresh token to localStorage —
   * shared across tabs. The original tab, still sitting on the result page,
   * kept its old in-memory token forever and went on sending a dead one, so
   * clicking the link appeared to do nothing and the page ground on until it
   * failed.
   *
   * Re-reading is cheap and localStorage is the shared source of truth.
   */
  getToken(): string | null {
    const stored = localStorage.getItem('fth_token')
    if (stored && stored !== this.token) {
      // Another tab signed in (or refreshed). Adopt it.
      this.token = stored
      this.authenticated = true
    } else if (!this.token) {
      this.token = stored
    }

    // An expired token is worse than none: it makes every request LOOK
    // authenticated and come back 401, instead of quietly taking the
    // anonymous path that already exists for these endpoints.
    //
    // Only clear when the STORED copy is the expired one. Clearing on a stale
    // in-memory value would delete a good token another tab had just written,
    // which is the exact handoff this method exists to get right.
    if (this.token && isTokenExpired(this.token)) {
      if (stored && isTokenExpired(stored)) {
        this.setToken(null)
      } else {
        this.token = stored
      }
    }
    return this.token
  }

  /** Mark as authenticated (e.g., after successful getMe() via cookie) */
  markAuthenticated() {
    this.authenticated = true
  }

  /**
   * Do we hold a login the SERVER will still accept?
   *
   * This used to be `token exists`, which is not the same question. A visitor
   * returning after their token aged out still had one sitting in
   * localStorage, so the client believed it was signed in, sent it to
   * authenticated endpoints, and got 401 "Token expired" back — which the anon
   * result page then printed at the user under "Something went wrong", on top
   * of a report that had completed successfully.
   *
   * `authenticated` is checked through getToken() rather than trusted on its
   * own, because it is also set by setToken() and would otherwise stay true
   * for a token that has since expired.
   */
  isAuthenticated(): boolean {
    const token = this.getToken()
    if (token) return true
    // Cookie-based session with no localStorage token: getMe() set this.
    return this.authenticated
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // ---- Sandbox interception -------------------------------------------
    // Every recruiter surface — table, drawers, filters — funnels through this
    // one method, so intercepting here turns the ENTIRE dashboard into the
    // public demo without editing a single component. See api/sandbox.ts.
    if (sb.isSandbox() && endpoint.startsWith('/batch')) {
      const method = (options.method || 'GET').toUpperCase()
      if (method !== 'GET') {
        // Writes are resolved locally and never sent. The server has no demo
        // write endpoint at all, so this is the only place they can land.
        return sandboxWrite<T>(endpoint, method, options.body)
      }
      // Reads go to the GET-only mirror, then local edits are replayed over
      // the real payload so the whole app sees one consistent world.
      endpoint = endpoint.replace(/^\/batch/, '/recruiter/sandbox')
      const fresh = await this.request<T>(endpoint, options)
      return sb.applyOverlay(fresh)
    }

    const token = this.getToken()
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      // Include Bearer token for backwards compatibility and non-cookie environments
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      // The demo's one re-weightable requirement. Sent on EVERY sandbox and
      // /recruiter/demo read rather than on the results call alone, so the
      // table, the drawer and the full report cannot end up describing
      // different criteria.
      ...(sb.isSandbox() && sb.reweightOff() ? { [sb.REWEIGHT_HEADER]: '1' } : {}),
      ...options.headers,
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      // Include credentials to send httpOnly cookies
      credentials: 'include',
    })

    if (!response.ok) {
      const error: any = await response.json().catch(() => ({
        detail: 'Network error',
      }))
      // FastAPI's HTTPException puts structured payloads under `detail`
      // (object with {error, message, ...}). Older error shapes use
      // top-level fields. Normalize both so ApiRequestError.code is
      // populated either way — without this, callers that inspect the
      // code (e.g., handleTailor detecting 'no_tailor_credits') break.
      const detail = error.detail
      const detailObj = detail && typeof detail === 'object' && !Array.isArray(detail) ? detail : null
      const code: string | undefined = error.error || detailObj?.error
      const message: string =
        error.message
        || detailObj?.message
        || (typeof detail === 'string' ? detail : 'Request failed')
      if (code) {
        throw new ApiRequestError(message, code, {
          limit: error.limit ?? detailObj?.limit,
          used: error.used ?? detailObj?.used,
          deviceUsed: error.device_used ?? detailObj?.device_used,
          deviceLimit: error.device_limit ?? detailObj?.device_limit,
          accountUsed: error.account_used ?? detailObj?.account_used,
          accountLimit: error.account_limit ?? detailObj?.account_limit,
          status: response.status,
        })
      }
      throw new ApiRequestError(message, 'unknown', { status: response.status })
    }

    return response.json()
  }

  // Auth endpoints
  async register(email: string, password: string, persona: string) {
    const data = await this.request<{
      access_token: string
      token_type: string
      expires_in: number
    }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, persona }),
    })
    this.setToken(data.access_token)
    return data
  }

  async login(email: string, password: string) {
    const data = await this.request<{
      access_token: string
      token_type: string
      expires_in: number
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    this.setToken(data.access_token)
    return data
  }

  async loginWithGoogle(idToken: string) {
    const data = await this.request<{
      access_token: string
      token_type: string
      expires_in: number
      is_new_user: boolean
      email: string
    }>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ id_token: idToken }),
    })
    this.setToken(data.access_token)
    return data
  }

  async sendEmailCode(email: string) {
    return this.request<{ message: string; expires_in: number }>('/auth/email-code/send', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  async loginWithLinkedIn(code: string, redirectUri: string) {
    const data = await this.request<{
      access_token: string
      token_type: string
      expires_in: number
      is_new_user: boolean
      email: string
    }>('/auth/linkedin', {
      method: 'POST',
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    })
    this.setToken(data.access_token)
    return data
  }

  async verifyEmailCode(email: string, code: string) {
    const data = await this.request<{
      access_token: string
      token_type: string
      expires_in: number
      is_new_user: boolean
      email: string
    }>('/auth/email-code/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    })
    this.setToken(data.access_token)
    return data
  }

  async getMe() {
    return this.request<{
      user_id: string
      email: string
      email_verified: boolean
      persona: string
      plan_tier: string
      first_name?: string | null
      last_name?: string | null
      notify_job_completed: boolean
      created_at: string
    }>('/auth/me')
  }

  async updateProfile(patch: {
    first_name?: string | null
    last_name?: string | null
    notify_job_completed?: boolean
  }) {
    return this.request<{
      user_id: string
      email: string
      first_name?: string | null
      last_name?: string | null
      notify_job_completed: boolean
    }>('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })
  }

  async listAuthIdentities(): Promise<{ identities: Array<{ provider: string; created_at: string | null }> }> {
    return this.request('/auth/identities')
  }

  // LinkedIn recruiter-bounce detector — binding + context endpoints.
  // Settings page reads/writes the bound handle so the user can see
  // and clear what the extension claimed on their behalf.
  async getLinkedInBinding(): Promise<{ handle: string | null; bound_at: string | null }> {
    return this.request('/user/linkedin-binding')
  }

  async setLinkedInBinding(handle: string | null): Promise<{ handle: string | null; bound_at: string | null }> {
    return this.request('/user/linkedin-binding', {
      method: 'POST',
      body: JSON.stringify({ handle }),
    })
  }

  async setPassword(password: string): Promise<{ status: string; message: string }> {
    return this.request('/auth/set-password', {
      method: 'POST',
      body: JSON.stringify({ password }),
    })
  }

  async logout() {
    // Clear httpOnly cookie via backend
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch {
      // Ignore errors - we're logging out anyway
    }
    // Clear local state
    this.setToken(null)
  }

  // Entitlements
  async getEntitlements() {
    return this.request<{
      user_id: string
      email: string | null
      plan_tier: string
      status: string
      is_active: boolean
      gpu_access: boolean
      limits: {
        jobs_per_day: number
        concurrent_jobs: number
        gpu_access: boolean
        features: string[]
      }
      current_period_end: string | null
      cancel_at_period_end: boolean
      has_stripe_billing: boolean
    }>('/entitlements/')
  }

  // Organization endpoints (recruiter)
  async createOrganization(name: string, slug: string) {
    return this.request<{
      org_id: string
      name: string
      slug: string
      owner_id: string
      plan_tier: string
      seat_limit: number
      seat_count: number
      is_active: boolean
      created_at: string
    }>('/organizations', {
      method: 'POST',
      body: JSON.stringify({ name, slug }),
    })
  }

  async listOrganizations() {
    return this.request<{
      organizations: Array<{
        org_id: string
        name: string
        slug: string
        owner_id: string
        plan_tier: string
        seat_limit: number
        seat_count: number
        is_active: boolean
        created_at: string
      }>
    }>('/organizations')
  }

  async inviteMember(orgId: string, email: string, role: 'admin' | 'member' = 'member') {
    return this.request<{
      member_id: string
      invite_email: string
      role: string
      invited_at: string
    }>(`/organizations/${orgId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    })
  }

  async listMembers(orgId: string) {
    return this.request<Array<{
      member_id: string
      user_id: string
      email: string
      role: string
      status: string
      joined_at: string | null
    }>>(`/organizations/${orgId}/members`)
  }

  // Analyze endpoints (job seeker)
  async createAnalysis(
    jdText: string,
    resumeText: string,
    jobTitle?: string,
    parentJobId?: string,
    skipPaidPipeline?: boolean
  ) {
    // Attach landing variant for A/B conversion tracking
    const landingVariant = typeof localStorage !== 'undefined'
      ? localStorage.getItem('fth_landing_variant') : null
    return this.request<{
      job_id: string
      status: string
      cached: boolean
      message: string
      version: number
      parent_job_id: string | null
    }>('/analyze', {
      method: 'POST',
      body: JSON.stringify({
        jd_text: jdText,
        resume_text: resumeText,
        job_title: jobTitle,
        parent_job_id: parentJobId,
        ...(skipPaidPipeline ? { skip_paid_pipeline: true } : {}),
        ...(landingVariant ? { landing_variant: landingVariant } : {}),
      }),
    })
  }

  async listAnalyses(options: {
    page?: number
    pageSize?: number
    search?: string
    dateFrom?: string
    dateTo?: string
    scoreMin?: number
    scoreMax?: number
    statusFilter?: string
    sortBy?: 'created_at' | 'score' | 'job_title' | 'company_name' | 'salary'
    sortOrder?: 'asc' | 'desc'
    /**
     * group_by='chain' folds tailor iterations into one application
     * row with versions[] populated. Dashboard uses this; admin /
     * forensics paths can keep group_by='none' for the flat view.
     */
    groupBy?: 'none' | 'chain'
  } = {}) {
    const {
      page = 1,
      pageSize = 20,
      search,
      dateFrom,
      dateTo,
      scoreMin,
      scoreMax,
      statusFilter,
      sortBy = 'created_at',
      sortOrder = 'desc',
      groupBy = 'none',
    } = options

    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('page_size', String(pageSize))
    params.set('sort_by', sortBy)
    params.set('sort_order', sortOrder)
    if (groupBy !== 'none') params.set('group_by', groupBy)

    if (search) params.set('search', search)
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)
    if (scoreMin !== undefined) params.set('score_min', String(scoreMin))
    if (scoreMax !== undefined) params.set('score_max', String(scoreMax))
    if (statusFilter) params.set('status_filter', statusFilter)

    return this.request<{
      analyses: Array<{
        job_id: string
        status: string
        job_title: string | null
        company_name: string | null
        resume_filename: string | null
        fittohire_score: number | null
        fittohire_label: string | null
        outcome_status: string | null
        applied_at: string | null
        created_at: string
        finished_at: string | null
        has_paid_report: boolean
        has_coaching: boolean
        interview_at: string | null
        contact_count: number
        compensation: {
          display?: string | null
          min_salary?: number | null
          max_salary?: number | null
          currency?: string | null
          pay_period?: string | null
        } | null
      }>
      total: number
      page: number
      page_size: number
      has_more: boolean
    }>(`/analyze/?${params.toString()}`)
  }

  async getAnalysisStatus(jobId: string) {
    return this.request<{
      job_id: string
      status: string
      job_title: string | null
      error_code: string | null
      error_detail: string | null
      created_at: string
      started_at: string | null
      finished_at: string | null
      version: number
      parent_job_id: string | null
    }>(`/analyze/${jobId}`)
  }

  async getAnalysisResults(jobId: string) {
    return this.request<{
      job_id: string
      status: string
      job_title: string | null
      company_name: string | null
      job_url: string | null
      compensation: {
        display?: string | null
        min_salary?: number | null
        max_salary?: number | null
        currency?: string | null
        pay_period?: string | null
      } | null
      version: number
      parent_job_id: string | null
      fittohire_score: number | null
      fittohire_label: string | null
      top_gaps: string[] | null
      full_gaps: Array<{ skill_id: string; skill_name: string }> | null
      explainability_table: ExplainabilityRow[] | null
      blockers: Blocker[] | null
      priority_ladder: PriorityItem[] | null
      coaching: CoachingSuggestion[] | null
      evidence: EvidenceItem[] | null
      // New insights pipeline data
      insights: InsightsData | null
      breakdown: SkillBreakdownItem[] | null
      expanded_fit: number | null
      extraction_evidence: {
        jd: ExtractionDetail[]
        resume: ExtractionDetail[]
      } | null
      extraction_diagnostics: {
        jd: Record<string, unknown>
        resume: Record<string, unknown>
      } | null
      // Graph-powered UI data
      graph_equivalents: Record<string, Array<{ concept_key: string; display_name: string; weight: number }>> | null
      boilerplate_stats: BoilerplateStats | null
      related_suggestions: RelatedSuggestion[] | null
      // LLM enrichment layer
      llm_enrichment: LLMEnrichment | null
      _gated_fields: string[] | null
      _upgrade_url: string | null
      _access_level: string | null
    }>(`/analyze/${jobId}/results`)
  }

  async getAnalysisText(jobId: string) {
    if (sb.isSandbox()) {
      // Ownership-scoped in the real app, so the demo's JD / Résumé /
      // Side-by-side buttons returned "not authenticated" — three dead
      // controls in the middle of the report.
      return this.request<{
        job_id: string
        jd_text: string
        resume_text: string
        job_title: string | null
      }>(`/recruiter/demo/text/${jobId}`)
    }
    return this.request<{
      job_id: string
      jd_text: string
      resume_text: string
      job_title: string | null
    }>(`/analyze/${jobId}/text`)
  }

  async getVersionHistory(jobId: string) {
    return this.request<{
      current_job_id: string
      root_job_id: string
      versions: Array<{
        job_id: string
        version: number
        status: string
        fittohire_score: number | null
        fittohire_label: string | null
        created_at: string
      }>
    }>(`/analyze/${jobId}/versions`)
  }

  async triggerPaidPipeline(jobId: string) {
    return this.request<{
      status: 'queued' | 'already_complete'
      job_id: string
    }>(`/analyze/${jobId}/trigger-paid`, {
      method: 'POST',
    })
  }

  // ── Tailor (resume rewrite) ────────────────────────────────────────
  // POST returns immediately with a run_id; client polls GET until 'done'.
  // Pro/enterprise tier is free; PAYG spends 1 tailor credit on success.

  async createTailor(jobId: string) {
    return this.request<{
      run_id: string
      status: 'queued'
      poll_url: string
    }>(`/analyze/${jobId}/tailor`, { method: 'POST', body: '{}' })
  }

  async getTailorStatus(runId: string) {
    return this.request<{
      run_id: string
      job_id: string
      status: 'queued' | 'running' | 'done' | 'failed'
      download_url: string | null
      change_manifest: string | null
      error_message: string | null
      elapsed_seconds: number | null
      created_at: string
      completed_at: string | null
      converted_from_pdf?: boolean
      reanalyzed_job_id?: string | null
    }>(`/tailor/${runId}`)
  }

  async getTailorText(runId: string, which: 'original' | 'tailored') {
    return this.request<{ text: string }>(`/tailor/${runId}/text?which=${which}`)
  }

  /**
   * Recruiter Readability — run on-demand against either an analysis
   * job or a saved base resume. Result is cached by content hash
   * server-side so same-content re-clicks return instantly with no
   * LLM call (response.cached === true).
   *
   * Pass force=true to bypass cache and run a fresh judge — used by
   * the modal's Re-check button so a user-initiated re-run always
   * gets a fresh result.
   */
  async checkReadabilityForJob(jobId: string, force = false) {
    if (sb.isSandbox()) {
      // The demo batch is warmed once, so there is nothing to compute and an
      // anonymous click must never be able to trigger work. Serving the cached
      // report keeps the button behaving exactly as it does for a recruiter
      // whose report already exists.
      return this.request<ReadabilityReport>(`/recruiter/demo/readability/${jobId}`)
    }
    const qs = force ? '?force=true' : ''
    return this.request<ReadabilityReport>(
      `/analyze/${jobId}/check-readability${qs}`,
      { method: 'POST', body: '{}' },
    )
  }

  /** Peek at cached readability for a job — returns null when no
   *  check has been run. Doesn't trigger any LLM work. Used by the
   *  detail panel to show the score on the button when available. */
  async getCachedReadabilityForJob(jobId: string): Promise<ReadabilityReport | null> {
    if (sb.isSandbox()) {
      // The real endpoint authorizes with get_current_user, which the sandbox
      // mount cannot substitute — so this is served by a dedicated public read
      // of the STORED report. Returning null (the first fix) silently removed
      // the bloat/keyword-stuffing detector from the demo, which is one of the
      // few things here no competitor does.
      return this.request<ReadabilityReport | null>(
        `/recruiter/demo/readability/${jobId}`)
    }
    return this.request<ReadabilityReport | null>(`/analyze/${jobId}/readability-cached`)
  }

  async checkReadabilityForResume(resumeId: string, force = false) {
    const qs = force ? '?force=true' : ''
    return this.request<ReadabilityReport>(
      `/analyze/resumes/${resumeId}/check-readability${qs}`,
      { method: 'POST', body: '{}' },
    )
  }

  async reanalyzeWithTailoredResume(runId: string) {
    return this.request<{
      job_id: string
      status: 'QUEUED'
      parent_job_id: string
      tailor_run_id: string
    }>(`/tailor/${runId}/reanalyze`, { method: 'POST', body: '{}' })
  }

  /**
   * Rename a tailored / edited resume. Used by:
   *   - TailorSuccessCard (right after tailor finishes)
   *   - ResumeManager "Tailored" tab (rename any time)
   * The new name is also what the re-analyze flow uses as the chained
   * job's resume_filename, so renaming before re-analyze matters.
   */
  async renameTailorRun(runId: string, displayName: string) {
    return this.request<DerivedResume>(`/tailor/${runId}`, {
      method: 'PATCH',
      body: JSON.stringify({ display_name: displayName }),
    })
  }

  /** Permanently delete a tailored / edited resume (R2 + soft-delete in DB). */
  async deleteTailorRun(runId: string): Promise<void> {
    await this.request<void>(`/tailor/${runId}`, { method: 'DELETE' })
  }

  /** List the user's tailored + edited resumes (the "Tailored" section). */
  /** Download several tailored resumes as one zip.
   *
   *  The single-run download is a 307 to a signed R2 URL, which a browser
   *  can only follow one at a time. The archive is built server-side, so
   *  this returns a Blob rather than navigating.
   */
  async downloadDerivedResumesBulk(runIds: string[]): Promise<Blob> {
    const token = this.getToken()
    const res = await fetch(`${API_BASE}/tailor/download-bulk`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ run_ids: runIds }),
    })
    if (!res.ok) {
      // The server names which resumes it could not serve; surface that
      // verbatim rather than a generic failure.
      const body: any = await res.json().catch(() => ({}))
      const detail = typeof body?.detail === 'string' ? body.detail : 'Download failed'
      throw new ApiRequestError(detail, 'unknown', { status: res.status })
    }
    return res.blob()
  }

  async listDerivedResumes() {
    // retention_days is the user's ACTUAL window, served by the API. The
    // banner used to hardcode "30 days standard / 60 days Pro" and both
    // numbers were wrong, which for a free user (7 days) meant being told
    // they had a month.
    return this.request<{ items: DerivedResume[]; retention_days: number }>('/tailor')
  }

  // Tailor balance / pricing / checkout — first tailor per JD is
  // included in the chain payment; additional tailors cost 1 credit
  // each from the $2/$8/$25 packs (or upgrade to Pro for unlimited).

  async getTailorBalance() {
    return this.request<{
      balance: number
      is_pro: boolean
    }>('/entitlements/tailor-balance')
  }

  async getTailorPricing() {
    return this.request<{
      skus: Array<{
        sku: 'tailor_1x' | 'tailor_5x' | 'tailor_20x'
        stripe_price_id: string
        credits: number
        price_usd: number
        per_tailor_usd: number
      }>
      currency: string
    }>('/entitlements/tailor-pricing')
  }

  async createTailorCheckout(priceId: string, successUrl: string, cancelUrl: string) {
    return this.request<{
      checkout_url: string
      session_id: string
    }>('/stripe/checkout/tailor', {
      method: 'POST',
      body: JSON.stringify({
        job_id: priceId,  // endpoint repurposes job_id to carry price_id
        success_url: successUrl,
        cancel_url: cancelUrl,
      }),
    })
  }

  async sendMagicLink(email: string, fingerprint: string) {
    return this.request<{ sent: boolean }>(`/auth/magic-link`, {
      method: 'POST',
      body: JSON.stringify({ email, fingerprint }),
    })
  }

  async createAccountFromAnon(jobId: string) {
    const data = await this.request<{
      access_token: string
      user_id: string
      email: string
      adopted_jobs: number
    }>('/auth/create-from-anon', {
      method: 'POST',
      body: JSON.stringify({ job_id: jobId }),
    })
    if (data.access_token) {
      this.setToken(data.access_token)
    }
    return data
  }

  async verifyMagicLink(token: string, fingerprint: string) {
    const data = await this.request<{
      session_id: string
      email: string
      expires_at: string
      access_token: string
      token_type: string
      expires_in: number
    }>(`/auth/magic-link/verify`, {
      method: 'POST',
      body: JSON.stringify({ token, fingerprint }),
    })
    this.setToken(data.access_token)
    return data
  }

  // Batch endpoints (recruiter)
  async listBatches(page = 1, pageSize = 20) {
    return this.request<{
      batches: Array<{
        batch_id: string
        status: string
        job_title: string | null
        company_name: string | null
        total_candidates: number
        completed_candidates: number
        failed_candidates: number
        created_at: string
        submitted_at: string | null
        finished_at: string | null
      }>
      total: number
      page: number
      page_size: number
      has_more: boolean
    }>(`/batch/?page=${page}&page_size=${pageSize}`)
  }

  /** Delete a position (batch) + its candidates. Owner-only on the server. */
  async deleteBatch(batchId: string) {
    return this.request<{ deleted: boolean; batch_id: string }>(
      `/batch/${batchId}`,
      { method: 'DELETE' },
    )
  }

  // ── Candidate pool (talent rediscovery + CRM) ────────────────────────
  async getPool(params: {
    q?: string; skill?: string[]; stage?: string; seniority?: string; hasReminder?: boolean
    sort?: string; page?: number; pageSize?: number
  } = {}) {
    const qs = new URLSearchParams()
    if (params.q) qs.set('q', params.q)
    ;(params.skill || []).forEach((s) => qs.append('skill', s))
    if (params.stage) qs.set('stage', params.stage)
    if (params.seniority) qs.set('seniority', params.seniority)
    if (params.hasReminder) qs.set('has_reminder', 'true')
    if (params.sort) qs.set('sort', params.sort)
    if (params.page) qs.set('page', String(params.page))
    if (params.pageSize) qs.set('page_size', String(params.pageSize))
    return this.request<{
      items: Array<{
        pool_entry_id: string; candidate_name: string | null; email: string | null
        phone: string | null; skill_count: number; top_skills: string[]
        position_count: number; version_count: number
        current_title: string | null; current_company: string | null
        location: string | null; seniority: string | null; stage: string; note: string | null
        remind_at: string | null; last_seen_at: string | null
      }>
      total: number; page: number; page_size: number; has_more: boolean
    }>(`/pool/?${qs.toString()}`)
  }

  async getPoolSkills(limit = 60) {
    return this.request<Array<{ skill_id: string; name: string; count: number }>>(
      `/pool/skills?limit=${limit}`,
    )
  }

  async matchPool(body: {
    batch_id?: string; skill_ids?: string[]; limit?: number; exclude_in_batch?: boolean
  }) {
    return this.request<{
      jd_skill_count: number; total_considered: number
      items: Array<{
        pool_entry_id: string; candidate_name: string | null; email: string | null
        skill_count: number; position_count: number; stage: string
        matched_skills: string[]; matched_count: number; coverage: number
      }>
    }>(`/pool/match`, { method: 'POST', body: JSON.stringify(body) })
  }

  async patchPoolEntry(poolEntryId: string, body: {
    stage?: string; note?: string; remind_at?: string | null; clear_remind?: boolean
  }) {
    return this.request<{
      pool_entry_id: string; candidate_name: string | null
      stage: string; note: string | null; remind_at: string | null
    }>(`/pool/${poolEntryId}`, { method: 'PATCH', body: JSON.stringify(body) })
  }

  async getPoolReminders() {
    return this.request<Array<{
      pool_entry_id: string; candidate_name: string | null; email: string | null
      stage: string; note: string | null; remind_at: string | null
    }>>(`/pool/reminders`)
  }

  async getPoolVersions(poolEntryId: string) {
    return this.request<Array<{
      pool_resume_id: string; applied_name: string | null; applied_email: string | null
      current_title: string | null; current_company: string | null
      skill_count: number; submitted_at: string | null; is_latest: boolean
    }>>(`/pool/${poolEntryId}/versions`)
  }

  async getPoolDuplicates() {
    return this.request<Array<{
      flagged: { pool_entry_id: string; candidate_name: string | null; emails: string[]; name_variants: string[]; versions: number }
      against: { pool_entry_id: string; candidate_name: string | null; emails: string[]; name_variants: string[]; versions: number } | null
      evidence: Record<string, unknown> | null
    }>>(`/pool/duplicates`)
  }

  async mergePoolEntry(poolEntryId: string, into?: string) {
    return this.request<{ merged_into: string; removed: string }>(
      `/pool/${poolEntryId}/merge`, { method: 'POST', body: JSON.stringify(into ? { into } : {}) },
    )
  }

  async rejectPoolDuplicate(poolEntryId: string) {
    return this.request<{ cleared: string }>(
      `/pool/${poolEntryId}/not-duplicate`, { method: 'POST' },
    )
  }

  async importStart() {
    return this.request<{ batch_id: string }>(`/pool/import/start`, { method: 'POST' })
  }

  async importFinalize(batchId: string) {
    return this.request<{ imported: number }>(`/pool/import/${batchId}/finalize`, { method: 'POST' })
  }

  async getPoolDetail(poolEntryId: string) {
    return this.request<{
      pool_entry_id: string; candidate_name: string | null
      emails: string[]; phones: string[]; name_variants: string[]
      current_title: string | null; current_company: string | null
      location: string | null; seniority: string | null
      stage: string; note: string | null; remind_at: string | null
      skills: Array<{ skill_id: string; name: string }>
      versions: Array<{
        pool_resume_id: string; applied_name: string | null; applied_email: string | null
        current_title: string | null; current_company: string | null
        skill_count: number; submitted_at: string | null; is_latest: boolean
      }>
      positions: Array<{
        batch_id: string; job_title: string | null; company_name: string | null
        stage: string; applied_at: string | null
      }>
    }>(`/pool/${poolEntryId}/detail`)
  }

  async getVersionResume(poolResumeId: string) {
    return this.request<{ filename: string; text: string }>(`/pool/version/${poolResumeId}/resume`)
  }

  async getBatchStatus(batchId: string) {
    return this.request<{
      batch_id: string
      status: string
      job_title: string | null
      company_name: string | null
      total_candidates: number
      completed_candidates: number
      failed_candidates: number
      created_at: string
      submitted_at: string | null
      finished_at: string | null
      jd_content_type?: string | null
    }>(`/batch/${batchId}`)
  }

  async getBatchPoolContext(batchId: string) {
    return this.request<{
      items: Array<{
        candidate_id: string; pool_entry_id: string; returning: boolean
        prior_positions: number; pool_stage: string; note: string | null
      }>
    }>(`/batch/${batchId}/pool-context`)
  }

  async getBatchResults(batchId: string) {
    return this.request<{
      batch_id: string
      status: string
      total_candidates: number
      completed_candidates: number
      failed_candidates: number
      candidates: Array<{
        candidate_id: string
        candidate_label: string
        candidate_name: string | null
        disposition: string | null
        stage: string
        job_id: string | null
        rank: number | null
        fittohire_score: number | null
        fittohire_label: string | null
        literal_fit: number | null
        expanded_fit: number | null
        matched_skills: string[] | null
        missing_skills: string[] | null
        // Per-level verdict counts + per-requirement items for hover
        // tooltips. Each item is one bar on the grid.
        requirement_breakdown: {
          required: {
            total: number; yes: number; partial: number; no: number
            items: Array<{ text: string; verdict: string; evidence: string | null }>
          }
          preferred: {
            total: number; yes: number; partial: number; no: number
            items: Array<{ text: string; verdict: string; evidence: string | null }>
          }
          responsibility: {
            total: number; yes: number; partial: number; no: number
            items: Array<{ text: string; verdict: string; evidence: string | null }>
          }
        } | null
        why: string | null
        claimed_count: number | null
        evidenced_count: number | null
        readability_score: number | null
        readability_label: string | null
        status: string
        error_code: string | null
      }>
    }>(`/batch/${batchId}/results`)
  }

  // Public, no-auth: the curated /recruiters/demo sample batch's real results.
  async getDemoResults() {
    return this.request<BatchResultsData>('/recruiter/demo')
  }

  // Which requirement the demo lets a visitor re-weight. Server-supplied so
  // re-seeding the demo cannot leave a control bound to a dead req_id.
  async getDemoConfig() {
    return this.request<{ reweight_req_id: string | null }>('/recruiter/demo/config')
  }

  async createBatch(payload: {
    jd_text?: string
    jd_filename?: string
    jd_content_type?: string
    job_title?: string
    company_name?: string
  }) {
    return this.request<{
      batch_id: string
      status: string
      jd_upload: {
        url: string
        r2_key: string
        expires_at: string
      } | null
    }>(`/batch/`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async requestBatchUploads(
    batchId: string,
    files: Array<{ filename: string; content_type: string }>,
  ) {
    return this.request<{
      batch_id: string
      uploads: Array<{
        filename: string
        url: string
        r2_key: string
        expires_at: string
      }>
    }>(`/batch/${batchId}/upload`, {
      method: 'POST',
      body: JSON.stringify({ files }),
    })
  }

  async submitBatch(batchId: string) {
    return this.request<{
      batch_id: string
      status: string
      total_candidates: number
      queued_at: string
    }>(`/batch/${batchId}/submit`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }

  // Pay-per-batch checkout ($5/100). Called when submitBatch returns a 402
  // (code "batch_payment_required"); redirect to the returned checkout_url.
  async createBatchCheckout(batchId: string, successUrl: string, cancelUrl: string) {
    return this.request<{ checkout_url: string; amount_cents: number }>(
      '/stripe/batch/checkout',
      {
        method: 'POST',
        body: JSON.stringify({ batch_id: batchId, success_url: successUrl, cancel_url: cancelUrl }),
      },
    )
  }

  // Subscribe to the Recruiter tier ($49/mo, unlimited batches). Price is
  // server-side; returns a Stripe checkout URL to redirect to. 503 if the
  // plan isn't configured in Stripe yet.
  async subscribeRecruiter(successUrl: string, cancelUrl: string) {
    return this.request<{ checkout_url: string; session_id: string }>(
      '/stripe/recruiter/subscribe',
      {
        method: 'POST',
        body: JSON.stringify({ success_url: successUrl, cancel_url: cancelUrl }),
      },
    )
  }

  // Tag the current user as a recruiter (persona=recruiter) so backend recruiter
  // gates agree with the UI. Called when the user switches into recruiter mode.
  async becomeRecruiter() {
    return this.request<{ user_id: string; persona: string }>('/auth/become-recruiter', {
      method: 'POST',
    })
  }

  batchCsvUrl(batchId: string): string {
    return `${API_BASE}${sandboxPath(`/batch/${batchId}/export/csv`)}`
  }

  async searchBatchCandidates(query: string, limit = 50) {
    const qPart = query ? `q=${encodeURIComponent(query)}&` : ''
    return this.request<{
      hits: Array<{
        batch_id: string
        candidate_id: string
        candidate_label: string
        candidate_name: string | null
        email: string | null
        job_title: string | null
        company_name: string | null
        disposition: string | null
        created_at: string
      }>
      total: number
      truncated: boolean
    }>(`/batch/search/candidates?${qPart}limit=${limit}`)
  }

  async getBatchCandidateContext(batchId: string, candidateId: string) {
    return this.request<RecruiterCandidateContext>(
      `/batch/${batchId}/candidates/${candidateId}`,
    )
  }

  async patchBatchCandidate(
    batchId: string,
    candidateId: string,
    body: { notes?: string; disposition?: string; stage?: string },
  ) {
    return this.request<RecruiterCandidateContext>(
      `/batch/${batchId}/candidates/${candidateId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      },
    )
  }

  async getCandidateResumeUrl(batchId: string, candidateId: string) {
    return this.request<{
      url: string
      expires_in: number
      content_type: string | null
      filename: string | null
    }>(`/batch/${batchId}/candidates/${candidateId}/resume-url`)
  }

  /** Update editable position metadata (title / company). */
  async patchBatch(batchId: string, body: { job_title?: string; company_name?: string }) {
    return this.request<{
      batch_id: string
      job_title: string | null
      company_name: string | null
      status: string
    }>(`/batch/${batchId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  }

  /** Generate the one-click hiring-manager brief for a position.
   *  Haiku call on the backend; ~5-7s typical. */
  async generateBatchBrief(batchId: string, opts: { topN?: number; scope?: 'score' | 'shortlist' } = {}) {
    if (sb.isSandbox()) {
      // A REAL brief, generated once by the owner and cached on the batch —
      // not filler. The public read never falls back to generating, because an
      // anonymous endpoint that can trigger an LLM call is a bill anyone can
      // run up.
      return this.request(`/recruiter/demo/brief`) as any
    }
    const params = new URLSearchParams()
    if (opts.topN) params.set('top_n', String(opts.topN))
    if (opts.scope) params.set('scope', opts.scope)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return this.request<{
      position_title: string | null
      company_name: string | null
      applicant_count: number
      candidates_included: number
      position_summary: string
      candidates: Array<{ name: string; narrative: string; ask_about?: string }>
    }>(`/batch/${batchId}/brief${qs}`, { method: 'POST', body: '{}' })
  }

  /** Recruiter activity feed — recent events derived from batch
   *  timestamps. Useful for the dashboard "things are alive" widget. */
  async getRecruiterActivity(limit = 15) {
    return this.request<{
      events: Array<{
        kind: 'position_created' | 'candidates_added' | 'screening_complete'
        timestamp: string
        batch_id: string
        job_title: string | null
        company_name: string | null
        detail: string | null
      }>
    }>(`/batch/activity?limit=${limit}`)
  }

  /** Bulk-advance a set of candidates to a non-terminal stage. The
   *  API enforces 'no auto-archive / no auto-reject' — passing 'rejected'
   *  or 'archived' returns 400 by design. */
  async bulkAdvanceStage(batchId: string, candidateIds: string[], stage: string) {
    return this.request<{ updated: number }>(
      `/batch/${batchId}/candidates/bulk-stage`,
      {
        method: 'POST',
        body: JSON.stringify({ candidate_ids: candidateIds, stage }),
      },
    )
  }

  /** Fetch the position's JD as HTML (DOCX rendered via mammoth, text
   *  wrapped in formatted paragraphs). For PDFs the endpoint 302s to a
   *  signed R2 URL — callers should detect that and iframe the URL
   *  directly rather than embedding via srcDoc. */
  async getBatchJdHtml(batchId: string): Promise<string> {
    const token = this.getToken()
    const response = await fetch(
      `${API_BASE}${sandboxPath(`/batch/${batchId}/jd-preview`)}`,
      {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
      },
    )
    if (!response.ok) {
      throw new ApiRequestError(`JD preview failed: HTTP ${response.status}`)
    }
    return response.text()
  }

  /** Raw bytes of an authenticated endpoint, for client-side rendering.
   *
   *  Used by DocxView: R2 signed URLs are a different origin so the SPA
   *  cannot fetch them under CORS, and docx-preview needs the actual file to
   *  lay out the document's own styles. */
  async getBlob(endpoint: string): Promise<Blob> {
    const token = this.getToken()
    // Blob fetches bypass request(), so the sandbox rewrite has to happen here
    // too. Missing this rendered the résumé pane as "Document fetch failed:
    // HTTP 401" in the demo while every other panel worked — the endpoint
    // builder returns a RELATIVE path with no API_BASE, so a search for
    // API_BASE-prefixed /batch/ literals did not surface it.
    const response = await fetch(`${API_BASE}${sandboxPath(endpoint)}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      credentials: 'include',
    })
    if (!response.ok) {
      throw new ApiRequestError(`Document fetch failed: HTTP ${response.status}`)
    }
    return response.blob()
  }

  /** Authenticated passthrough of a candidate's résumé file, unconverted. */
  getCandidateResumeRawUrl(batchId: string, candidateId: string): string {
    return sandboxPath(`/batch/${batchId}/candidates/${candidateId}/resume-raw`)
  }

  /** Direct (browser-fetchable) URL for the JD preview — used by iframe
   *  src when source is a PDF (we let the 302 happen in the iframe). */
  getBatchJdPreviewUrl(batchId: string): string {
    return `${API_BASE}${sandboxPath(`/batch/${batchId}/jd-preview`)}`
  }

  /** Whether the position's JD source is PDF (so iframe src works) vs
   *  DOCX/text (so we must fetch the HTML and use srcDoc). Read from
   *  the status endpoint. */
  async getBatchJdContentType(batchId: string): Promise<string | null> {
    const status = await this.getBatchStatus(batchId)
    return status.jd_content_type ?? null
  }

  /** Batch-aware candidate analysis. Returns the override-filtered
   *  unified_requirements and recomputed score so the detail page's
   *  Analysis section and headline number reflect recruiter edits. */
  async getBatchCandidateAnalysis(batchId: string, candidateId: string) {
    return this.request<{
      candidate_id: string
      job_id: string | null
      score: number | null
      unified_requirements: Array<Record<string, unknown>>
    }>(`/batch/${batchId}/candidates/${candidateId}/analysis`)
  }

  /** Fetch the candidate resume as HTML (DOCX rendered server-side via
   *  mammoth). Returned text is dropped into an iframe srcDoc so the
   *  iframe doesn't need a subresource URL — avoids the
   *  upgrade-insecure-requests CSP breaking http://localhost dev. */
  async getCandidateResumeHtml(batchId: string, candidateId: string): Promise<string> {
    const token = this.getToken()
    const response = await fetch(
      `${API_BASE}${sandboxPath(`/batch/${batchId}/candidates/${candidateId}/resume-preview`)}`,
      {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
      },
    )
    if (!response.ok) {
      throw new ApiRequestError(`Resume preview failed: HTTP ${response.status}`)
    }
    return response.text()
  }

  async getBatchRequirements(batchId: string) {
    return this.request<{
      batch_id: string
      requirements: Array<{
        req_id: string
        text: string
        level: string
        original_level: string
        is_deleted: boolean
        is_recategorized: boolean
      }>
    }>(`/batch/${batchId}/requirements`)
  }

  async patchBatchRequirements(
    batchId: string,
    overrides: Array<{ req_id: string; action: 'recategorize' | 'delete' | 'reset'; new_level?: string }>,
  ) {
    return this.request<{
      batch_id: string
      requirements: Array<{
        req_id: string
        text: string
        level: string
        original_level: string
        is_deleted: boolean
        is_recategorized: boolean
      }>
    }>(`/batch/${batchId}/requirements`, {
      method: 'PATCH',
      body: JSON.stringify({ overrides }),
    })
  }

  // Try endpoints (anonymous "Holy Shit" funnel)
  async submitTry(jdText: string, resumeText: string, skipPaidPipeline?: boolean, resumeId?: string) {
    // Attach landing variant for A/B conversion tracking
    const landingVariant = typeof localStorage !== 'undefined'
      ? localStorage.getItem('fth_landing_variant') : null
    return this.request<{
      job_id: string
      status: string
      message: string
    }>('/try/', {
      method: 'POST',
      body: JSON.stringify({
        jd_text: jdText,
        resume_text: resumeText,
        ...(skipPaidPipeline ? { skip_paid_pipeline: true } : {}),
        ...(landingVariant ? { landing_variant: landingVariant } : {}),
        ...(resumeId ? { resume_id: resumeId } : {}),
      }),
    })
  }

  async parseFile(file: File): Promise<{ text: string; filename: string; chars: number }> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${API_BASE}/try/parse-file`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }))
      throw new ApiRequestError(
        typeof error.detail === 'string' ? error.detail : 'Failed to parse file'
      )
    }

    return response.json()
  }

  async getTryStatus(jobId: string) {
    if (sb.isSandbox()) {
      // Every demo job is DONE — the batch is fully scored and warmed —
      // and the real status routes are ownership-scoped, so an anonymous
      // visitor got 'Job not found' and the report never rendered.
      return { status: 'DONE', error_detail: null } as any
    }
    return this.request<{
      job_id: string
      status: string
      error_detail: string | null
    }>(`/try/${jobId}`)
  }

  async getTryReveal(jobId: string) {
    return this.request<{
      job_id: string
      status: string
      jd_skill_count: number
      resume_matched_count: number
      must_missing_count: number
      top_skills: Array<{
        skill_name: string
        requirement: string
        matched: boolean
      }>
      teaser_text: string
    }>(`/try/${jobId}/reveal`)
  }

  async claimTry(jobId: string) {
    return this.request<{
      job_id: string
      claimed: boolean
      message: string
    }>('/try/claim', {
      method: 'POST',
      body: JSON.stringify({ job_id: jobId }),
    })
  }

  // Stripe checkout. Affiliate attribution is server-side (fth_ref cookie).
  async createCheckoutSession(priceId: string, successUrl: string, cancelUrl: string) {
    const payload: Record<string, string> = {
      price_id: priceId,
      success_url: successUrl,
      cancel_url: cancelUrl,
    }
    return this.request<{
      checkout_url: string
      session_id: string
    }>('/stripe/checkout', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async createBillingPortalSession() {
    return this.request<{
      portal_url: string
    }>('/stripe/portal', {
      method: 'POST',
    })
  }

  async getPaymentMethod() {
    return this.request<{
      brand: string | null
      last4: string | null
      exp_month: number | null
      exp_year: number | null
      has_card: boolean
    }>('/stripe/payment-method')
  }

  async createSetupIntent() {
    return this.request<{
      client_secret: string
    }>('/stripe/setup-intent', { method: 'POST' })
  }

  async setDefaultPaymentMethod(paymentMethodId: string) {
    return this.request<{ success: boolean }>('/stripe/payment-method/default', {
      method: 'POST',
      body: JSON.stringify({ payment_method_id: paymentMethodId }),
    })
  }

  async getInvoices() {
    return this.request<{
      invoices: Array<{
        invoice_id: string
        amount_cents: number
        status: string
        created_at: string
        pdf_url: string | null
      }>
    }>('/stripe/invoices')
  }

  async verifyCheckout(sessionId: string) {
    return this.request<{
      verified: boolean
      stripe_paid: boolean
      webhook_received: boolean
      credits_applied: boolean
      credits_balance: number
      plan_tier: string
      message: string
    }>('/stripe/verify-checkout', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    })
  }

  async createCreditPaymentIntent(priceId: string) {
    return this.request<{
      client_secret: string
      payment_intent_id: string
      amount: number
      currency: string
      description: string
    }>('/stripe/payment-intent/credits', {
      method: 'POST',
      body: JSON.stringify({ price_id: priceId }),
    })
  }

  async createSubscriptionIntent() {
    return this.request<{
      client_secret: string
      subscription_id: string
      amount: number
      currency: string
    }>('/stripe/subscription-intent', { method: 'POST' })
  }

  // Single analysis checkout (for anonymous users paying for one job)
  async createSingleAnalysisCheckout(jobId: string, successUrl: string, cancelUrl: string) {
    const payload: Record<string, string> = {
      job_id: jobId,
      success_url: successUrl,
      cancel_url: cancelUrl,
    }
    return this.request<{
      checkout_url: string
      session_id: string
    }>('/stripe/checkout/single-analysis', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async createCreditCheckout(priceId: string, successUrl: string, cancelUrl: string) {
    const payload: Record<string, string> = {
      job_id: priceId,  // carries price_id to the credits endpoint
      success_url: successUrl,
      cancel_url: cancelUrl,
    }
    return this.request<{
      checkout_url: string
      session_id: string
    }>('/stripe/checkout/credits', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  // Affiliate partner portal — the logged-in affiliate's own stats + assets.
  async getAffiliateMe() {
    return this.request<{
      ref_code: string
      referral_link: string
      promo_code: string | null
      commission_rate: number
      is_recurring: boolean
      payout_handle: string | null
      status: string
      stats: {
        clicks: number
        conversions: number
        earned_cents: number
        pending_cents: number
        paid_cents: number
      }
      conversions: {
        date: string | null
        label: string
        amount_cents: number
        commission_cents: number
        status: string
        clears_at: string | null
        paid_at: string | null
      }[]
      swipe_copy: {
        recruiter: string[]
        candidate: string[]
      }
    }>('/affiliates/me')
  }

  async updateAffiliatePayoutHandle(payoutHandle: string) {
    return this.request<{ payout_handle: string }>('/affiliates/me', {
      method: 'PATCH',
      body: JSON.stringify({ payout_handle: payoutHandle }),
    })
  }

  // Self-serve: apply to the affiliate program (creates a pending affiliate).
  async applyToAffiliateProgram(input: {
    email: string
    name?: string
    applicationNote?: string
    turnstileToken?: string
  }) {
    return this.request<{ status: string; ref_code: string }>('/affiliates/apply', {
      method: 'POST',
      body: JSON.stringify({
        email: input.email,
        name: input.name,
        application_note: input.applicationNote,
        turnstile_token: input.turnstileToken,
      }),
    })
  }

  // Create Payment Intent for Stripe Elements checkout
  async createPaymentIntent(jobId: string) {
    return this.request<{
      client_secret: string | null
      payment_intent_id: string
      amount: number
      currency: string
      already_paid: boolean
    }>('/stripe/payment-intent/single-analysis', {
      method: 'POST',
      body: JSON.stringify({ job_id: jobId }),
    })
  }

  // Poll paid_status after payment confirmation (with per-artifact status)
  // Routes to auth endpoint for logged-in users, anon endpoint otherwise
  /** The recruiter-facing narrative on a candidate. Cached; never generates. */
  async getRecruiterNarrative(batchId: string, jobId: string) {
    if (sb.isSandbox()) {
      return this.request<RecruiterNarrative>(`/recruiter/demo/narrative/${jobId}`)
    }
    return this.request<RecruiterNarrative>(`/batch/${batchId}/jobs/${jobId}/narrative`)
  }

  /** Generate it. One Haiku call — only ever from an explicit button press. */
  async generateRecruiterNarrative(batchId: string, jobId: string) {
    if (sb.isSandbox()) {
      // The demo is pre-run and must never be able to trigger a billed call,
      // so "generate" resolves to the cached read.
      return this.request<RecruiterNarrative>(`/recruiter/demo/narrative/${jobId}`)
    }
    return this.request<RecruiterNarrative>(
      `/batch/${batchId}/jobs/${jobId}/narrative`, { method: 'POST', body: '{}' })
  }

  async getPaidStatus(jobId: string) {
    if (sb.isSandbox()) {
      // Already generated and stored; nothing to poll for.
      return { paid_status: 'paid_complete', evidence_map_status: 'done',
               gap_context_status: 'done', priority_fix_status: 'done',
               error: null } as any
    }
    if (this.isAuthenticated()) {
      return this.request<{
        paid_status: 'not_paid' | 'pending' | 'processing' | 'paid_complete' | 'failed'
        evidence_map_status: 'pending' | 'done' | 'error' | null
        gap_context_status: 'pending' | 'done' | 'error' | null
        priority_fix_status: 'pending' | 'done' | 'error' | null
        error: string | null
      }>(`/try/${jobId}/paid-status`)
    }
    return this.request<{
      paid_status: 'not_paid' | 'pending' | 'processing' | 'paid_complete' | 'failed'
      evidence_map_status: 'pending' | 'done' | 'error' | null
      gap_context_status: 'pending' | 'done' | 'error' | null
      priority_fix_status: 'pending' | 'done' | 'error' | null
      error: string | null
    }>(`/try/anon/${jobId}/paid-status`, {
      headers: { 'X-Anon-Session-Id': this.getAnonSessionId() },
    })
  }

  // Get paid artifacts after unlock complete
  // Routes to auth endpoint for logged-in users, anon endpoint otherwise
  async getPaidArtifacts(jobId: string) {
    if (sb.isSandbox()) {
      // The real call takes an ANON path when unauthenticated and 403s on a
      // job it does not own — and it is awaited inside a Promise.all with the
      // requirement analysis, so that one rejection blanked the entire drawer.
      //
      // Serve the genuinely stored artifacts instead. All ten demo jobs are
      // paid_status='done' with artifacts in R2, so stubbing this to null (the
      // first fix) threw away real computed output and made the demo thinner
      // than the product it sells.
      return this.request(`/recruiter/demo/paid-artifacts/${jobId}`) as any
    }
    if (this.isAuthenticated()) {
      return this.request<{
        job_id: string
        paid_status: string
        artifacts: any | null
        error: string | null
        job_title: string | null
        company_name: string | null
        resume_filename: string | null
        resume_version_id: string | null
        outcome_notes: string | null
        is_owner: boolean
      }>(`/try/${jobId}/paid-artifacts`)
    }
    const response = await fetch(`${API_BASE}/try/anon/${jobId}/paid-artifacts`, {
      headers: {
        'X-Anon-Session-Id': this.getAnonSessionId(),
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to fetch artifacts' }))
      throw new Error(error.detail || 'Failed to fetch artifacts')
    }

    return response.json() as Promise<{
      job_id: string
      paid_status: string
      artifacts: Record<string, any> | null
      error: string | null
    }>
  }

  // Get pricing from server (no auth required)
  async getPricing() {
    return this.request<{
      scan_price_usd: number
      currency: string
    }>('/entitlements/pricing')
  }

  async getUsage() {
    return this.request<{
      user_id: string
      plan_tier: string
      jobs_today: number
      jobs_limit: number
      jobs_remaining: number
      concurrent_jobs: number
      concurrent_limit: number
      credits_balance: number
    }>('/entitlements/usage')
  }

  async cancelSubscription() {
    return this.request<{
      canceled: boolean
      cancel_at_period_end: boolean
      current_period_end: string | null
      message: string
    }>('/stripe/cancel', {
      method: 'POST',
    })
  }

  async reactivateSubscription() {
    return this.request<{
      reactivated: boolean
      message: string
    }>('/stripe/reactivate', {
      method: 'POST',
    })
  }

  // Share link endpoints
  async createShareLink(jobId: string, expiresDays = 30) {
    return this.request<{
      token: string
      share_url: string
      expires_at: string | null
      view_count: number
    }>(`/jobs/${jobId}/share`, {
      method: 'POST',
      body: JSON.stringify({ expires_days: expiresDays }),
    })
  }

  async getShareLink(jobId: string) {
    return this.request<{
      token: string
      share_url: string
      expires_at: string | null
      view_count: number
    } | null>(`/jobs/${jobId}/share`)
  }

  // =================================================================
  // ANONYMOUS (ANON) FLOW ENDPOINTS
  // =================================================================

  private getAnonSessionId(): string {
    const key = 'fth_anon_session_id'
    let sessionId = localStorage.getItem(key)
    if (!sessionId) {
      sessionId = crypto.randomUUID()
      localStorage.setItem(key, sessionId)
    }
    return sessionId
  }

  async submitAnon(jdText: string, resumeText: string) {
    const response = await fetch(`${API_BASE}/try/anon/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Anon-Session-Id': this.getAnonSessionId(),
      },
      credentials: 'include',
      body: JSON.stringify({
        jd_text: jdText,
        resume_text: resumeText,
      }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }))
      if (error.error) {
        throw new ApiRequestError(error.message || 'Request failed', error.error)
      }
      throw new ApiRequestError(
        typeof error.detail === 'string' ? error.detail : error.detail?.message || 'Request failed'
      )
    }

    return response.json() as Promise<{
      job_id: string
      status: string
      message: string
      anon_session_id: string
    }>
  }

  async getAnonStatus(jobId: string) {
    if (sb.isSandbox()) {
      // Every demo job is DONE — the batch is fully scored and warmed —
      // and the real status routes are ownership-scoped, so an anonymous
      // visitor got 'Job not found' and the report never rendered.
      return { status: 'DONE', error_detail: null } as any
    }
    const response = await fetch(`${API_BASE}/try/anon/${jobId}/status`, {
      headers: {
        'X-Anon-Session-Id': this.getAnonSessionId(),
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }))
      throw new ApiRequestError(
        typeof error.detail === 'string' ? error.detail : 'Request failed'
      )
    }

    return response.json() as Promise<{
      job_id: string
      status: string
      error_detail: string | null
      progress_pct: number | null
    }>
  }

  async getAnonTeaser(jobId: string) {
    const response = await fetch(`${API_BASE}/try/anon/${jobId}/teaser`, {
      headers: {
        'X-Anon-Session-Id': this.getAnonSessionId(),
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }))
      throw new ApiRequestError(
        typeof error.detail === 'string' ? error.detail : 'Request failed'
      )
    }

    return response.json() as Promise<{
      job_id: string
      status: string
      required_detected: number
      required_total: number
      inferred_matches_count: number
      risk_level: string
      high_priority_gaps_count: number
      semantic_proof: {
        category: string
        requirement_level: string
        match_type: string
        confidence: number
        ats_sensitivity: string
      } | null
    }>
  }

  async getFullFreeReport(jobId: string) {
    const token = this.getToken()
    const response = await fetch(`${API_BASE}/try/${jobId}/report`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }))
      if (response.status === 403) {
        throw new ApiRequestError('Report not unlocked', 'not_unlocked')
      }
      throw new ApiRequestError(
        typeof error.detail === 'string' ? error.detail : 'Request failed'
      )
    }

    return response.json() as Promise<{
      job_id: string
      status: string
      job_title: string | null
      evaluation_summary: string
      total_requirements: number
      requirements_satisfied: number
      critical_gaps: number
      requirements: Array<{
        skill_id: string
        skill_name: string
        requirement_level: string
        satisfied: boolean
        match_type: string | null
        evidence_source: string | null
        confidence: number | null
      }>
      ats_diagnosis: {
        overall_risk: string
        terminology_mismatches: Array<{
          requirement: string
          issue: string
          explanation: string
        }>
        indirect_evidence_risks: Array<{
          requirement: string
          risk: string
          explanation: string
        }>
      }
      evaluation_method: string
    }>
  }

  async getFreePreview(jobId: string) {
    const token = this.getToken()
    const response = await fetch(`${API_BASE}/try/${jobId}/preview`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }))
      if (response.status === 403) {
        throw new ApiRequestError('Preview not unlocked', 'not_unlocked')
      }
      throw new ApiRequestError(
        typeof error.detail === 'string' ? error.detail : 'Request failed'
      )
    }

    return response.json() as Promise<{
      job_id: string
      job_title: string | null
      company_name: string | null
      resume_filename: string | null
      resume_version_id: string | null
      outcome_notes: string | null
      is_owner: boolean
      alignment_label: 'STRONG' | 'MIXED' | 'WEAK'
      literal_fit_percent: number
      skills_matched: number
      gaps_found: number
      proof_points: number
      top_matches: string[]
    }>
  }

  async requestUnlock(jobId: string, email: string, fingerprint: string) {
    const response = await fetch(`${API_BASE}/try/anon/${jobId}/request-unlock`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Anon-Session-Id': this.getAnonSessionId(),
      },
      credentials: 'include',
      body: JSON.stringify({ email, fingerprint }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }))
      if (error.error) {
        throw new ApiRequestError(error.message || 'Request failed', error.error)
      }
      throw new ApiRequestError(
        typeof error.detail === 'string' ? error.detail : error.detail?.message || 'Request failed'
      )
    }

    return response.json() as Promise<{
      sent: boolean
      message: string
    }>
  }

  async verifyUnlock(token: string, fingerprint: string) {
    const data = await this.request<{
      job_id: string
      unlocked: boolean
      message: string
    }>('/auth/unlock/verify', {
      method: 'POST',
      body: JSON.stringify({ token, fingerprint }),
    })
    return data
  }

  // =================================================================
  // TRACKER ENDPOINTS
  // =================================================================

  async updateTracker(jobId: string, data: {
    company_name?: string
    job_title?: string
    resume_filename?: string
    applied_at?: string
    outcome_status?: string
    outcome_notes?: string
    follow_up_at?: string
    job_url?: string
    interview_at?: string
  }) {
    return this.request<{
      job_id: string
      message: string
      outcome_status: string | null
      company_name: string | null
      job_title: string | null
    }>(`/analyze/tracker/${jobId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async createManualEntry(data: {
    company_name: string
    job_title: string
    resume_filename?: string
    applied_at?: string
    outcome_status?: string
    outcome_notes?: string
    interview_at?: string
  }) {
    return this.request<{
      job_id: string
      message: string
      outcome_status: string | null
      company_name: string | null
      job_title: string | null
    }>('/analyze/tracker/manual', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getTrackerList(options: {
    page?: number
    pageSize?: number
    outcomeFilter?: string
  } = {}) {
    const params = new URLSearchParams()
    params.set('page', String(options.page || 1))
    params.set('page_size', String(options.pageSize || 50))
    if (options.outcomeFilter) params.set('outcome_filter', options.outcomeFilter)

    return this.request<{
      items: Array<{
        job_id: string
        job_title: string | null
        company_name: string | null
        resume_filename: string | null
        fittohire_score: number | null
        fittohire_label: string | null
        outcome_status: string | null
        outcome_notes: string | null
        outcome_updated_at: string | null
        applied_at: string | null
        created_at: string
        status: string
        has_paid_report: boolean
      }>
      total: number
      page: number
      page_size: number
      has_more: boolean
    }>(`/analyze/tracker/list?${params.toString()}`)
  }

  async getTrackerStats() {
    return this.request<{
      total: number
      applied: number
      interviewing: number
      offered: number
      accepted: number
      rejected: number
      no_response: number
      withdrawn: number
      avg_score_overall: number | null
      avg_score_interview: number | null
      avg_score_no_response: number | null
    }>('/analyze/tracker/stats')
  }

  async bulkDeleteAnalyses(jobIds: string[]): Promise<{ deleted: number; errors: string[] }> {
    return this.request<{ deleted: number; errors: string[] }>('/analyze/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ job_ids: jobIds }),
    })
  }

  async bulkUpdateStatus(jobIds: string[], outcomeStatus: string): Promise<{ updated: number; errors: string[] }> {
    return this.request<{ updated: number; errors: string[] }>('/analyze/bulk/status', {
      method: 'PATCH',
      body: JSON.stringify({ job_ids: jobIds, outcome_status: outcomeStatus }),
    })
  }

  async downloadTrackerXlsx(): Promise<Blob> {
    const token = this.getToken()
    const response = await fetch(`${API_BASE}/analyze/tracker/export`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Export failed' }))
      throw new ApiRequestError(
        typeof error.detail === 'string' ? error.detail : 'Failed to export tracker'
      )
    }

    return response.blob()
  }

  // Account forensics - cross-job analytics
  async getForensics() {
    return this.request<{
      total_jobs: number
      funnel: Array<{ status: string; count: number; pct: number }>
      score_buckets: Array<{
        range: string; count: number
        interview_count: number; offer_count: number
        interview_rate: number | null; offer_rate: number | null
      }>
      top_missing_skills: Array<{ skill: string; frequency: number; pct: number }>
      resume_versions: Array<{
        filename: string; count: number
        avg_score: number | null; interview_count: number
      }>
      timeline: Array<{
        month: string; count: number
        avg_score: number | null; interview_count: number
      }>
      avg_score: number | null
      highest_score: number | null
      interview_rate: number | null
    }>('/analyze/forensics')
  }

  // PDF export - returns blob for direct download
  async downloadPdf(jobId: string): Promise<Blob> {
    const token = this.getToken()
    const response = await fetch(`${API_BASE}/analyze/${jobId}/export/pdf`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Download failed' }))
      if (error.error === 'upgrade_required') {
        throw new ApiRequestError(error.message || 'Upgrade required', 'upgrade_required')
      }
      throw new ApiRequestError(
        typeof error.detail === 'string' ? error.detail : 'Failed to download PDF'
      )
    }

    return response.blob()
  }

  // ── Resume Storage ──────────────────────────────────────────────

  async listResumes(): Promise<{
    resumes: Array<{
      resume_id: string
      name: string
      is_default: boolean
      file_size_bytes: number | null
      has_original_file: boolean
      original_filename: string | null
      created_at: string
      updated_at: string
    }>
    default_resume_id: string | null
  }> {
    return this.request('/analyze/resumes')
  }

  async uploadResume(file: File): Promise<{
    resume_id: string
    name: string
    is_default: boolean
    has_original_file: boolean
    original_filename: string | null
  }> {
    const formData = new FormData()
    formData.append('file', file)
    const token = this.getToken()
    const resp = await fetch(`${API_BASE}/analyze/resumes/upload`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ detail: 'Upload failed' }))
      throw new Error(err.detail || 'Upload failed')
    }
    return resp.json()
  }

  getResumeDownloadUrl(resumeId: string): string {
    return `${API_BASE}/analyze/resumes/${resumeId}/download`
  }

  async saveResume(name: string, resumeText: string): Promise<{
    resume_id: string
    name: string
    is_default: boolean
    file_size_bytes: number | null
    created_at: string
    updated_at: string
  }> {
    return this.request('/analyze/resumes', {
      method: 'POST',
      body: JSON.stringify({ name, resume_text: resumeText }),
    })
  }

  async getDefaultResume(): Promise<{
    resume_id: string
    name: string
    resume_text: string
  }> {
    return this.request('/analyze/resumes/default')
  }

  async getResumeText(resumeId: string): Promise<{
    resume_id: string
    name: string
    resume_text: string
  }> {
    return this.request(`/analyze/resumes/${resumeId}`)
  }

  async renameResume(resumeId: string, name: string): Promise<{
    resume_id: string
    name: string
    is_default: boolean
  }> {
    return this.request(`/analyze/resumes/${resumeId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    })
  }

  async setDefaultResume(resumeId: string): Promise<{
    resume_id: string
    name: string
    is_default: boolean
  }> {
    return this.request(`/analyze/resumes/${resumeId}/default`, {
      method: 'POST',
    })
  }

  async deleteResume(resumeId: string): Promise<void> {
    return this.request(`/analyze/resumes/${resumeId}`, {
      method: 'DELETE',
    })
  }

  // ── Bulk URL Import ─────────────────────────────────────────────

  async bulkImportUrls(urls: string[]): Promise<{
    results: Array<{
      url: string
      job_id?: string
      title?: string
      company?: string
      status: string
      error?: string
    }>
    total_requested: number
    total_succeeded: number
    total_failed: number
  }> {
    return this.request('/analyze/bulk-import', {
      method: 'POST',
      body: JSON.stringify({ urls }),
    })
  }
  // ── Run Analysis on Saved Job ────────────────────────────────────

  async runAnalysisOnJob(jobId: string, options?: { jd_text?: string; resume_id?: string }): Promise<{
    job_id: string
    status: string
    message: string
    jd_source: string
    resume_name: string
  }> {
    return this.request(`/analyze/${jobId}/run`, {
      method: 'POST',
      body: JSON.stringify(options || {}),
    })
  }

  /**
   * Rescore a completed job with a (potentially different) resume.
   * Creates a NEW job linked via parent_job_id so both versions live in
   * the dashboard side by side.
   *
   * Pass the source job's metadata (job_title, company_name, job_url)
   * explicitly so the new analysis inherits them verbatim instead of
   * letting /analyze/'s extractor re-derive from the JD — the classifier
   * can pick different values from the same text ('NXP' vs 'NXP
   * Semiconductors'), making rescored versions look like different jobs.
   */
  async rescoreJob(
    jobId: string,
    resumeId: string,
    source?: {
      job_title?: string | null
      company_name?: string | null
      job_url?: string | null
      outcome_status?: string | null
      outcome_notes?: string | null
      applied_at?: string | null
      follow_up_at?: string | null
      interview_at?: string | null
    },
  ): Promise<{
    job_id: string
    status: string
    message: string
  }> {
    const [src, resume] = await Promise.all([
      this.getAnalysisText(jobId),
      this.getResumeText(resumeId),
    ])
    if (!src.jd_text) throw new Error('Original JD text not available')
    if (!resume.resume_text) throw new Error('Resume text not available')

    const result = await this.request<{
      job_id: string
      status: string
      message: string
    }>('/analyze/', {
      method: 'POST',
      body: JSON.stringify({
        jd_text: src.jd_text,
        resume_text: resume.resume_text,
        job_title: source?.job_title || src.job_title || undefined,
        company_name: source?.company_name || undefined,
        job_url: source?.job_url || undefined,
        resume_id: resumeId,
        parent_job_id: jobId,
        skip_paid_pipeline: true,
      }),
    })

    // Clone tracker fields (URL, outcome status, notes, dates) onto the
    // new job so the line item feels like a continuation of the
    // original, not a stranger. Awaited so callers that reload the
    // page right after don't race the patch.
    if (source) {
      const tracker: Record<string, string> = {}
      if (source.job_url) tracker.job_url = source.job_url
      if (source.outcome_status) tracker.outcome_status = source.outcome_status
      if (source.outcome_notes) tracker.outcome_notes = source.outcome_notes
      if (source.applied_at) tracker.applied_at = source.applied_at
      if (source.follow_up_at) tracker.follow_up_at = source.follow_up_at
      if (source.interview_at) tracker.interview_at = source.interview_at
      if (Object.keys(tracker).length) {
        try { await this.updateTracker(result.job_id, tracker) } catch { /* non-fatal */ }
      }
    }

    return result
  }

  // ── Contacts ──────────────────────────────────────────────────────

  async listContacts(jobId: string): Promise<{
    contacts: Array<{
      contact_id: string
      name: string
      email: string | null
      phone: string | null
      role: string | null
      linkedin_url: string | null
      last_contacted_at: string | null
      notes: string | null
      created_at: string
      updated_at: string
    }>
    total: number
  }> {
    return this.request(`/analyze/${jobId}/contacts`)
  }

  async createContact(jobId: string, data: {
    name: string
    email?: string
    phone?: string
    role?: string
    linkedin_url?: string
    notes?: string
  }): Promise<{
    contact_id: string
    name: string
    email: string | null
    phone: string | null
    role: string | null
    linkedin_url: string | null
    last_contacted_at: string | null
    notes: string | null
    created_at: string
    updated_at: string
  }> {
    return this.request(`/analyze/${jobId}/contacts`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateContact(jobId: string, contactId: string, data: {
    name?: string
    email?: string
    phone?: string
    role?: string
    linkedin_url?: string
    last_contacted_at?: string
    notes?: string
  }): Promise<{
    contact_id: string
    name: string
    email: string | null
    phone: string | null
    role: string | null
    linkedin_url: string | null
    last_contacted_at: string | null
    notes: string | null
    created_at: string
    updated_at: string
  }> {
    return this.request(`/analyze/${jobId}/contacts/${contactId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteContact(jobId: string, contactId: string): Promise<void> {
    return this.request(`/analyze/${jobId}/contacts/${contactId}`, {
      method: 'DELETE',
    })
  }

  async getGapInsights(): Promise<{
    total_analyzed: number
    top_missing_skills: Array<{
      skill_name: string
      missing_in: number
      total_jobs: number
      percentage: number
      example_jobs: string[]
    }>
    top_matched_skills: Array<{
      skill_name: string
      matched_in: number
      total_jobs: number
      percentage: number
    }>
    score_distribution: {
      strong: number
      good: number
      mixed: number
      weak: number
    }
    avg_score_by_outcome: Record<string, number>
    insight_text: string | null
  }> {
    return this.request('/analyze/insights/gaps')
  }

  // ── Actions API (data-driven per-job buttons) ────────────────────
  // The server returns the action rows for a job; the frontend
  // <ActionButton> dispatches based on action.id / action.on_success.

  async getJobActions(jobId: string) {
    return this.request<{
      job_id: string
      actions: Array<{
        id: string
        label: string
        icon: string
        color: string
        variant: string
        endpoint: string
        method: string
        input_kind: 'none' | 'file'
        input_accept: string | null
        on_success: string
        on_success_template: string | null
        available: boolean
        reason_unavailable: string | null
        cost_label: string | null
      }>
    }>(`/analyze/${jobId}/actions`)
  }

  /**
   * Generic dispatcher for an action returned by /actions. Body is
   * either undefined (input_kind='none') or FormData with 'file'
   * (input_kind='file'). Returns the raw JSON response — the caller's
   * on_success handler interprets it.
   */
  async fireAction(
    endpoint: string,
    method: string,
    body?: FormData,
  ): Promise<any> {
    const token = this.getToken()
    const headers: Record<string, string> = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
    const init: RequestInit = {
      method: method.toUpperCase(),
      headers,
      credentials: 'include',
    }
    if (body instanceof FormData) {
      init.body = body
      // Don't set Content-Type — browser sets multipart boundary.
    }
    // endpoint is server-supplied and starts with '/v1/...'; strip the
    // leading /v1 because API_BASE already includes it.
    const path = endpoint.startsWith('/v1') ? endpoint.slice(3) : endpoint
    const resp = await fetch(`${API_BASE}${path}`, init)
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ detail: 'Action failed' }))
      throw new ApiRequestError(
        typeof err.detail === 'string' ? err.detail : 'Action failed',
        err.error,
      )
    }
    return resp.json().catch(() => ({}))
  }
}

export const api = new ApiClient()
export default api
